import React, { useState, useEffect, useRef, useCallback } from 'react';
import DOMPurify from 'dompurify';
import logger from '../../utils/logger';
import "../medication/Medication.css";

const TextVoiceSearchAllergies = ({ csvData: propCsvData, label, description, onSelectionChange, initialSelections = []  }) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState({ gen: [], brands: [], correction: null });
    const [selectedItems, setSelectedItems] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [pendingGeneric, setPendingGeneric] = useState('');
    const [currentList, setCurrentList] = useState([]);
    const [isListening, setIsListening] = useState(false);
    const [audioLevels, setAudioLevels] = useState(new Array(15).fill(0));
    const [showQuick, setShowQuick] = useState(true);
    const recognitionRef = useRef(null);
    const audioContextRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const animationFrameRef = useRef(null);
    const timeoutRef = useRef(null);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
    const [countdown, setCountdown] = useState(10);
    const countdownIntervalRef = useRef(null);
    const [showAllCommon, setShowAllCommon] = useState(false);
    const isInitialMount = useRef(true);

    // FIXED: Initialize selectedItems from initialSelections
    useEffect(() => {
        if (initialSelections && initialSelections.length > 0) {
            setSelectedItems(initialSelections);
        }
    }, [initialSelections]);

    // Sync selectedItems to parent when they change - but use a debounced approach
    useEffect(() => {
        // Skip calling onSelectionChange on initial mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        if (onSelectionChange) {
            const timeoutId = setTimeout(() => {
                onSelectionChange(selectedItems);
            }, 300); // Debounce for 300ms
            
            return () => clearTimeout(timeoutId);
        }
    }, [selectedItems]);

    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 768);
        };
        window.addEventListener("resize", handleResize);
        
        return () => {
            window.removeEventListener("resize", handleResize);
            stopListening();
        };
    }, []);

    // Initialize speech recognition
    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (SpeechRecognition) {
            try {
                const recognition = new SpeechRecognition();
                recognition.lang = 'en-US';
                recognition.interimResults = true;
                recognition.maxAlternatives = 1;
                recognition.continuous = true;

                recognition.onresult = (event) => {
                    const transcript = event.results[event.results.length - 1][0].transcript || '';
                    setQuery(transcript);
                };

                recognition.onerror = (event) => {
                    logger.log('Speech recognition error:', event.error);
                    if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
                        alert('Microphone access denied. Please allow microphone permissions in your browser settings.');
                        stopListening();
                    }
                };

                recognitionRef.current = recognition;
            } catch (e) {
                logger.error('Error initializing speech recognition:', e);
            }
        } else {
            logger.log('Speech recognition not available - will use fallback method');
        }

        return () => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.abort();
                } catch (e) {
                    logger.log('Cleanup error:', e);
                }
            }
        };
    }, []);

    const csvData = propCsvData?.records
        ? propCsvData
        : {
            records: [],
            genericBrands: {},
            commonGenerics: [],
            misspell: {},
            soundAlikePairs: []
        };

    const dlev = (a, b) => {
        const s1 = (a || '').toLowerCase().trim();
        const s2 = (b || '').toLowerCase().trim();
        const m = s1.length,
            n = s2.length;
        const dp = Array(m + 1)
            .fill(0)
            .map(() => Array(n + 1).fill(0));

        for (let i = 0; i <= m; i++) dp[i][0] = i;
        for (let j = 0; j <= n; j++) dp[0][j] = j;

        for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
                dp[i][j] =
                    s1[i - 1] === s2[j - 1]
                        ? dp[i - 1][j - 1]
                        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
            }
        }
        return dp[m][n];
    };

    const scoreEntry = (name, q) => {
        const n = (name || '').toLowerCase().trim();
        const s = (q || '').toLowerCase().trim();
        if (!s) return 1000000;
        if (n === s) return -5;
        if (n.startsWith(s)) return -4;
        if (n.includes(s)) return -3;

        const corr = csvData.misspell[s];
        if (corr && corr.toLowerCase() === n) return -2.5;

        return dlev(n, s);
    };

    const search = (q) => {
        const s = (q || '').toLowerCase().trim();
        if (!s) return { gen: [], brands: [], correction: null };

        const correction = csvData.misspell[s] || null;
        const allGenerics = Object.keys(csvData.genericBrands);

        const gen = allGenerics
            .map((g) => [g, scoreEntry(g, s)])
            .filter(([_, sc]) => sc < 6)
            .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
            .slice(0, 12);

        const brandHits = csvData.records
            .filter((r) => r.Brand)
            .map((r) => [r.Brand, r.Generic, r.Type, scoreEntry(r.Brand, s)])
            .filter(([_, __, ___, sc]) => sc < 5)
            .sort((a, b) => a[3] - b[3] || a[0].localeCompare(b[0]))
            .slice(0, 10);

        return { gen, brands: brandHits, correction };
    };

    const highlight = (text, q) => {
        if (!q || !text) return DOMPurify.sanitize(String(text || ''));
        const clean = DOMPurify.sanitize(String(text));
        const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
        return clean.replace(
            regex,
            '<mark style="background:#fde68a;color:#111827;padding:0 2px;border-radius:3px;">$1</mark>'
        );
    };

    useEffect(() => {
        if (csvData.records?.length > 0) {
            const results = search(query);
            setSuggestions(results);
            setCurrentList([]);
            setActiveIndex(-1);

            const list = [];
            results.gen.forEach(([g]) => list.push({ type: 'generic', value: g }));
            results.brands.forEach(([b, g]) => list.push({ type: 'brand', value: b, generic: g }));
            setCurrentList(list);

            const bestGen = results.gen.length
                ? results.gen[0][0]
                : results.brands.length
                    ? results.brands[0][1]
                    : '';
            setPendingGeneric(bestGen || '');
            setShowQuick(!query);
        }
    }, [query, csvData]);

    const handleKeyDown = (e) => {
        if (!currentList.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(Math.min(currentList.length - 1, activeIndex + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(Math.max(0, activeIndex - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0) {
                const item = currentList[activeIndex];
                item.type === 'generic' ? chooseGeneric(item.value) : chooseBrand(item.generic, item.value);
            } else if (pendingGeneric) {
                addGeneric();
            }
        }
    };

    const chooseGeneric = (generic) => setPendingGeneric(generic);

    const chooseBrand = (generic, brand) => {
        addItem(generic, brand);
        postAdd();
    };

    const addGeneric = () => {
        if (pendingGeneric) {
            addItem(pendingGeneric, '');
            postAdd();
        }
    };

    const addGenericDirectly = (generic) => {
        if (selectedItems.some(item => item.generic === generic)) {
            return;
        }
        
        addItem(generic, '');
        setQuery('');
        setPendingGeneric('');
        setShowQuick(true);
    };

    const addItem = (generic, brand) => {
        const id = generic + '|' + (brand || '');
        if (selectedItems.some((item) => item.id === id)) return;

        const types = new Set(csvData.records.filter((r) => r.Generic === generic).map((r) => r.Type).filter(Boolean));
        const type = types.size === 1 ? [...types][0] : types.size > 1 ? 'Mixed' : '';

        const newItem = {
            id,
            generic,
            brand: brand || '',
            type,
            form: '',
            strength: '',
            route: '',
            dose: '',
            units: '',
            frequency: '',
            prn: 'No',
            prn_reason: '',
            status: '',
            notes: ''
        };

        setSelectedItems([...selectedItems, newItem]);
    };

    const postAdd = () => {
        setQuery('');
        setPendingGeneric('');
        setShowQuick(true);
    };

    const removeItem = (id) => {
        setSelectedItems(selectedItems.filter((item) => item.id !== id));
    };

    const updateItem = (id, field, value) => {
        setSelectedItems(selectedItems.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
    };

    const stopListening = () => {
        setIsListening(false);
        setCountdown(10);
        
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        
        setAudioLevels(new Array(15).fill(0));
    };

    const startListening = async () => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        
        if (!SpeechRecognition && !recognitionRef.current) {
            alert('Speech recognition is not supported in your browser (Firefox doesn\'t support it). The microphone will record audio for 15 seconds. Please speak your medication name clearly.');
        }
        
        if (isListening) {
            return;
        }

        setIsListening(true);
        setCountdown(10);
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioCtx;
            
            const analyser = audioCtx.createAnalyser();
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);
            analyser.fftSize = 32;
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const updateLevels = () => {
                if (!audioContextRef.current || audioContextRef.current.state === 'closed') return;
                analyser.getByteFrequencyData(dataArray);
                const newLevels = Array.from(dataArray).map(v => v / 255);
                setAudioLevels(newLevels.slice(0, 15));
                animationFrameRef.current = requestAnimationFrame(updateLevels);
            };
            updateLevels();

            if (recognitionRef.current) {
                try {
                    recognitionRef.current.start();
                } catch (e) {
                    logger.log('Speech recognition not available, continuing with visual feedback only');
                }
            }
            
            countdownIntervalRef.current = setInterval(() => {
                setCountdown(prev => {
                    if (prev <= 1) {
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            
            timeoutRef.current = setTimeout(() => {
                if (recognitionRef.current) {
                    try {
                        recognitionRef.current.stop();
                    } catch (e) {
                        logger.log('Error stopping recognition on timeout:', e);
                    }
                }
                if (!recognitionRef.current) {
                    alert('Recording complete. Note: Firefox doesn\'t support automatic speech-to-text. Please type your medication name manually.');
                }
                stopListening();
            }, 10000);
                        
        } catch (e) {
            logger.error('Error starting speech recognition:', e);
            alert('Could not access microphone. Please check your browser permissions.');
            stopListening();
        }
    };

    const getSoundAlikeWarning = (generic) => {
        const soundMap = new Map();
        csvData.soundAlikePairs.forEach(([a, b]) => {
            const k1 = a.toLowerCase(),
                k2 = b.toLowerCase();
            if (!soundMap.has(k1)) soundMap.set(k1, new Set());
            if (!soundMap.has(k2)) soundMap.set(k2, new Set());
            soundMap.get(k1).add(b);
            soundMap.get(k2).add(a);
        });
        const conf = soundMap.get(generic.toLowerCase());
        return conf?.size ? [...conf] : [];
    };

    const displayedGenerics = isDesktop || showAllCommon 
        ? csvData.commonGenerics 
        : csvData.commonGenerics.slice(0, 3);

    const hasMoreGenerics = csvData.commonGenerics.length > 3;

    return (
        <div className='medication-container'>
            <header className='medication-header'>
                <div className='wrap'>
                    <h1 className='medication-title'>{label}</h1>
                    <div className='medication-subtitle'>
                       {description}
                    </div>
                </div>
            </header>

            <main>
                <div>
                    {(!csvData.records || csvData.records.length === 0) && (
                        <div className='csvUpload'>
                            <h3>Upload CSV File</h3>
                            <p style={{ color: '#475569', marginBottom: '15px' }}>
                                Upload a CSV file with medication data (Type, Category, Class, Generic, Brand columns)
                            </p>
                        </div>
                    )}

                    {csvData.records && csvData.records.length > 0 && (
                        <section className='medication-card'>
                            <div className='medication-searchbar'>
                                <div className='medication-field'>
                                    <input
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="e.g., ibupro..., lisin..., melat..."
                                        className='medication-input'
                                    />

                                    {isListening && (
                                        <div className="audio-wave">
                                            {audioLevels.map((level, i) => (
                                                <div
                                                    key={i}
                                                    style={{
                                                        width: "15px",
                                                        height: `${3 + level * 40}px`,
                                                        backgroundColor: "#06A788",
                                                        borderRadius: "2px 2px 0 0",
                                                    }}
                                                />
                                            ))}
                                            <div className='countdown-container'>
                                                {countdown}s
                                            </div>
                                        </div>
                                    )}

                                    <button
                                        onClick={startListening}
                                        disabled={isListening}
                                        className={`mic ${isListening ? "mic-active" : ""}`}
                                    >
                                        <svg
                                            width="40"
                                            height="40"
                                            viewBox="0 0 512 512"
                                            fill="none"
                                            className={isListening ? "text-red-500" : "text-black"}
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M237.541 328.897C262.669 328.897 284.173 319.951 302.064 302.067C319.952 284.183 328.897 262.668 328.897 237.542V91.365C328.897 66.239 319.959 44.733 302.064 26.84C284.173 8.951 262.669 0 237.541 0C212.416 0 190.909 8.951 173.017 26.84C155.124 44.73 146.179 66.239 146.179 91.365V237.542C146.179 262.667 155.128 284.183 173.017 302.067C190.906 319.951 212.416 328.897 237.541 328.897Z" fill="currentColor" />
                                            <path d="M396.563 188.15C392.957 184.533 388.665 182.724 383.716 182.724C378.772 182.724 374.49 184.533 370.869 188.15C367.256 191.766 365.448 196.048 365.448 200.995V237.542C365.448 272.756 352.93 302.875 327.9 327.904C302.878 352.934 272.755 365.449 237.54 365.449C202.326 365.449 172.206 352.934 147.175 327.904C122.147 302.882 109.634 272.757 109.634 237.542V200.995C109.634 196.048 107.825 191.766 104.21 188.15C100.593 184.533 96.3149 182.724 91.3629 182.724C86.4109 182.724 82.1279 184.533 78.5129 188.15C74.8949 191.766 73.0869 196.048 73.0869 200.995V237.542C73.0869 279.607 87.1269 316.201 115.199 347.318C143.272 378.436 177.961 396.279 219.267 400.844V438.535H146.178C141.229 438.535 136.947 440.346 133.331 443.963C129.714 447.577 127.905 451.861 127.905 456.81C127.905 461.751 129.714 466.043 133.331 469.657C136.947 473.271 141.229 475.085 146.178 475.085H328.897C333.845 475.085 338.133 473.272 341.744 469.657C345.365 466.044 347.175 461.752 347.175 456.81C347.175 451.862 345.365 447.578 341.744 443.963C338.134 440.346 333.846 438.535 328.897 438.535H255.817V400.844C297.116 396.279 331.802 378.436 359.878 347.318C387.954 316.201 401.998 279.607 401.998 237.542V200.995C401.998 196.049 400.185 191.77 396.563 188.15Z" fill="currentColor" />
                                        </svg>

                                        <p>Speak to search</p>
                                    </button>

                                </div>
                            </div>

                            {suggestions.correction && (
                                <div className='medication-'>
                                    Did you mean <strong>{suggestions.correction}</strong>? Look for it below.
                                </div>
                            )}

                            {showQuick && csvData.commonGenerics && csvData.commonGenerics.length > 0 && (
                                <details className='details' open={isDesktop}>
                                    <summary style={{
                                        cursor: 'pointer',
                                        padding: '10px 14px',   
                                        backgroundColor: '#fff',
                                        border: '1px solid #e2e8f0',
                                        fontWeight: 600,
                                        userSelect: 'none',
                                        color: '#0f172a',
                                    }}>
                                        Choose from top most common
                                    </summary>
                                    <div style={{
                                        border: '1px solid #e2e8f0',
                                        borderTop: 'none',
                                        padding: '10px',
                                        backgroundColor: '#f8fafc',
                                    }}>
                                        {displayedGenerics.map(generic => {
                                            const isSelected = selectedItems.some(item => item.generic === generic);
                                            return (
                                                <div
                                                    key={generic}
                                                    className='chip'
                                                    onClick={() => addGenericDirectly(generic)}
                                                    style={{
                                                        backgroundColor: isSelected ? '#dcfce7' : '',
                                                        border: isSelected ? '1px solid #16a34a' : '',
                                                        color: isSelected ? '#15803d' : '',
                                                        opacity: isSelected ? 0.8 : 1,
                                                        cursor: isSelected ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {generic}
                                                    {isSelected && <span style={{ marginLeft: '6px', fontSize: '0.8rem' }}>✓</span>}
                                                </div>
                                            );
                                        })}
                                        
                                        {!isDesktop && hasMoreGenerics && (
                                            <button
                                                onClick={() => setShowAllCommon(!showAllCommon)}
                                                className='chip show-more-btn'
                                            >
                                                {showAllCommon ? 'Show Less' : `Show More `}
                                            </button>
                                        )}
                                    </div>
                                </details>
                            )}

                            {query && (
                                <>
                                    {(suggestions.gen.length > 0 || suggestions.brands.length > 0) && (
                                        <div className='suggestions'>
                                            {suggestions.gen.map(([generic, score], index) => {
                                                const types = new Set(csvData.records.filter(r => r.Generic === generic).map(r => r.Type).filter(Boolean));
                                                const type = types.size === 1 ? [...types][0] : (types.size > 1 ? "Mixed" : "");
                                                const brandCount = csvData.genericBrands[generic]?.length || 0;
                                                
                                                return (
                                                    <div
                                                        key={`generic-${generic}`}
                                                        className='sitem'
                                                        style={{
                                                            outline: activeIndex === index ? '2px solid #0ea5e9' : 'none',
                                                        }}
                                                        onClick={() => addGenericDirectly(generic)}
                                                    >
                                                        <div style={{display: 'flex', flexDirection: 'column', gap: '4px', flex: 1}}>
                                                            <div style={{fontWeight: 700, color: '#0f172a'}}>
                                                                <span dangerouslySetInnerHTML={{__html: `Generic: ${highlight(generic, query)}`}} />
                                                                {type && <span className='pill'>{type}</span>}
                                                            </div>
                                                            <div style={{color: '#475569', fontSize: '0.95rem'}}>
                                                                Tap to choose. {brandCount} brand option(s) available.
                                                            </div>
                                                        </div>
                                                        <span className='pill'>Generic</span>
                                                    </div>
                                                );
                                            })}
                                            
                                            {suggestions.brands.map(([brand, generic, type], index) => {
                                                const adjustedIndex = suggestions.gen.length + index;
                                                return (
                                                    <div
                                                        key={`brand-${brand}-${generic}`}
                                                        className='sitem'
                                                        style={{
                                                            outline: activeIndex === adjustedIndex ? '2px solid #0ea5e9' : 'none',
                                                            ...(index === suggestions.brands.length - 1 ? {borderBottom: 'none'} : {})
                                                        }}
                                                        onClick={() => chooseBrand(generic, brand)}
                                                    >
                                                        <div style={{display: 'flex', flexDirection: 'column', gap: '4px', flex: 1}}>
                                                            <div style={{fontWeight: 700, color: '#0f172a'}}>
                                                                <span dangerouslySetInnerHTML={{__html: `Brand: ${highlight(brand, query)}`}} />
                                                                {type && <span className='pill'>{type}</span>}
                                                            </div>
                                                            <div style={{color: '#475569', fontSize: '0.95rem'}}>
                                                                Generic: <strong>{generic}</strong> — tap to select brand or choose generic to be safe.
                                                            </div>
                                                        </div>
                                                        <span className='pill'>Brand</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {pendingGeneric && (
                                        <button className='bigbtn' onClick={addGeneric}>
                                            Add the generic "{pendingGeneric}"
                                        </button>
                                    )}

                                    {pendingGeneric && csvData.genericBrands[pendingGeneric]?.length > 0 && (
                                        <div style={{ marginTop: '8px' }}>
                                            <div style={{ color: '#475569', fontSize: '0.95rem', marginBottom: '8px' }}>
                                                Pick a brand if you recognize it (optional):
                                            </div>
                                            {csvData.genericBrands[pendingGeneric].map(brand => (
                                                <div
                                                    key={brand}
                                                    className='chip'
                                                    onClick={() => chooseBrand(pendingGeneric, brand)}
                                                >
                                                    {brand}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            {selectedItems.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    {selectedItems.map(item => {
                                        const soundAlikes = getSoundAlikeWarning(item.generic);
                                        return (
                                            <div key={item.id} className='selectedCard'>
                                                <div className='chip-container'>
                                                    <div className='m-flex'>
                                                        <strong>{item.generic}</strong>
                                                        {item.brand && <span style={{ color: '#475569' }}> — {item.brand}</span>}
                                                        {item.type && <span className='chip' style={{ marginLeft: '8px' }}>{item.type}</span>}
                                                    </div>
                                                    <button
                                                        style={{
                                                            border: '1px solid #fecaca',
                                                            backgroundColor: '#fff5f5',
                                                            color: '#991b1b',
                                                            padding: '8px 10px',
                                                            borderRadius: '10px',
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={() => removeItem(item.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>

                                                {soundAlikes.length > 0 && (
                                                    <div className='warning'>
                                                        <strong>Look-alike/Sound-alike alert:</strong> This can be confused with {soundAlikes.join(', ')}. Please double-check.
                                                    </div>
                                                )}

                                                <details style={{ marginTop: '8px' }}>
                                                    <summary style={{
                                                        cursor: 'pointer',
                                                        padding: '8px',
                                                        backgroundColor: '#f8fafc',
                                                        borderRadius: '8px',
                                                        fontWeight: 600,
                                                        color: '#334155',
                                                        border: '1px solid #e2e8f0',
                                                    }}>
                                                        Details (optional)
                                                    </summary>
                                                    <div className='formRow'>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                         <label style={{ color: '#475569', fontSize: '0.85rem' }}>Form</label>
                                                            <select
                                                                className='select'
                                                                value={item.form}
                                                                onChange={(e) => updateItem(item.id, 'form', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option>Tablet</option>
                                                                <option>Capsule</option>
                                                                <option>Solution</option>
                                                                <option>Suspension</option>
                                                                <option>Injection</option>
                                                                <option>Patch</option>
                                                                <option>Cream</option>
                                                                <option>Ointment</option>
                                                                <option>Gel</option>
                                                                <option>Device</option>
                                                            </select>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>    
                                                        
                                                        <label style={{ color: '#475569', fontSize: '0.85rem' }}>Strength</label>
                                                            <input
                                                                style={{ padding: '10px' }}
                                                                className='select'
                                                                type="text"
                                                                value={item.strength}
                                                                onChange={(e) => updateItem(item.id, 'strength', e.target.value)}
                                                                placeholder="e.g., 10 mg"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>Route</label>
                                                            <select
                                                                className='select'
                                                                value={item.route}
                                                                onChange={(e) => updateItem(item.id, 'route', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option>Oral</option>
                                                                <option>Topical</option>
                                                                <option>Subcutaneous</option>
                                                                <option>Intramuscular</option>
                                                                <option>Intravenous</option>
                                                                <option>Inhalation</option>
                                                                <option>Ophthalmic</option>
                                                                <option>Otic</option>
                                                                <option>Nasal</option>
                                                                <option>Rectal</option>
                                                                <option>Transdermal</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className='formRow'>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>Dose amount</label>
                                                            <input
                                                                style={{ padding: '10px' }}
                                                                className='select'
                                                                type="text"
                                                                value={item.dose}
                                                                onChange={(e) => updateItem(item.id, 'dose', e.target.value)}
                                                                placeholder="e.g., 1"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>Units</label>
                                                            <select
                                                                className='select'
                                                                value={item.units}
                                                                onChange={(e) => updateItem(item.id, 'units', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option>mg</option>
                                                                <option>mcg</option>
                                                                <option>g</option>
                                                                <option>mL</option>
                                                                <option>units</option>
                                                                <option>puffs</option>
                                                                <option>drops</option>
                                                                <option>patch</option>
                                                            </select>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>Frequency</label>
                                                            <select
                                                                className='select'
                                                                value={item.frequency}
                                                                onChange={(e) => updateItem(item.id, 'frequency', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option>Once daily</option>
                                                                <option>Twice daily</option>
                                                                <option>Three times daily</option>
                                                                <option>Four times daily</option>
                                                                <option>Every other day</option>
                                                                <option>Weekly</option>
                                                                <option>Monthly</option>
                                                                <option>As directed</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className='formRow'>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>PRN (as needed)</label>
                                                            <select
                                                                className='select'
                                                                value={item.prn}
                                                                onChange={(e) => updateItem(item.id, 'prn', e.target.value)}
                                                            >
                                                                <option>No</option>
                                                                <option>Yes</option>
                                                            </select>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>PRN reason</label>
                                                            <input
                                                                style={{ padding: '10px' }}
                                                                className='select'
                                                                type="text"
                                                                value={item.prn_reason}
                                                                onChange={(e) => updateItem(item.id, 'prn_reason', e.target.value)}
                                                                placeholder="e.g., pain, anxiety"
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <label style={{ color: '#475569', fontSize: '0.85rem' }}>Status</label>
                                                            <select
                                                                className='select'
                                                                value={item.status}
                                                                onChange={(e) => updateItem(item.id, 'status', e.target.value)}
                                                            >
                                                                <option value="">Select</option>
                                                                <option>Active</option>
                                                                <option>As needed</option>
                                                                <option>Completed</option>
                                                                <option>Discontinued</option>
                                                                <option>Unknown</option>
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div style={{ marginTop: '8px' }}>
                                                        <label style={{ color: '#475569', fontSize: '0.85rem' }}>Notes</label>
                                                        <input
                                                            style={{ padding: '10px', marginTop: '6px' }}
                                                            className='select'
                                                            type="text"
                                                            value={item.notes}
                                                            onChange={(e) => updateItem(item.id, 'notes', e.target.value)}
                                                            placeholder="Optional"
                                                        />
                                                    </div>
                                                </details>

                                                <div style={{
                                                    marginTop: '10px',
                                                    background: '#f1f5f9',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    color: '#334155',
                                                    lineHeight: 1.5,
                                                }}>
                                                    <strong>Preview:</strong>{' '}
                                                    {[
                                                        item.generic,
                                                        item.brand && `— ${item.brand}`,
                                                        item.type && `— ${item.type}`,
                                                        item.form && `— ${item.form}`,
                                                        item.strength && `— ${item.strength}`,
                                                        item.route && `— ${item.route}`,
                                                        item.dose && `— ${item.dose}`,
                                                        item.units && `— ${item.units}`,
                                                        item.frequency && `— ${item.frequency}`,
                                                        item.prn === 'Yes' && `— PRN`,
                                                        item.prn_reason && `(Reason: ${item.prn_reason})`,
                                                        item.status && `— Status: ${item.status}`,
                                                        item.notes && `— Notes: ${item.notes}`,
                                                    ]
                                                        .filter(Boolean)
                                                       .join(' ')}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </main>
        </div>
    );
};

export default TextVoiceSearchAllergies;